import { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";
import { Property } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { foldText } from "src/components/FacetFilters/foldText";

export const UNGROUPED_OPTION_KEY = "-";

export type GroupedDataListOptions = Record<
    string,
    {
        label: string;
        options: DataListOption[];
    }
>;

// Option finder functions
export const findOptionById = (options: DataListOption[], id: number | undefined): DataListOption | undefined => {
    if (!id) return undefined;
    return options.find((option) => option.id === id) ?? undefined;
};

export const findOptionsByIds = (options: DataListOption[], ids: number[] = []): DataListOption[] => {
    return options.filter((option) => ids.includes(option.id));
};

const getEquivalentOptionCodes = (code: string): string[] => {
    if (code.startsWith("extra:")) {
        return [code, code.replace(/^extra:/, "")];
    }

    return [code, `extra:${code}`];
};

export const findOptionByCode = (options: DataListOption[], code: string | undefined): DataListOption | undefined => {
    if (!code) return undefined;

    const exactMatch = options.find((option) => option.code === code);
    if (exactMatch) return exactMatch;

    const equivalentCodes = getEquivalentOptionCodes(code);
    return options.find((option) => equivalentCodes.includes(option.code)) ?? undefined;
};

export const findOptionsByCodes = (options: DataListOption[], codes: string[] = []): DataListOption[] => {
    if (!codes.length) return [];

    const equivalentCodes = new Set(codes.flatMap(getEquivalentOptionCodes));

    return options.filter((option) => equivalentCodes.has(option.code));
};

export const findOptionByValue = (options: DataListOption[], value: string | undefined): DataListOption | undefined => {
    if (!value) return undefined;
    return options.find((option) => option.value === value) ?? undefined;
};

export const findOptionsByValues = (options: DataListOption[], values: string[]): DataListOption[] => {
    if (!values.length) return [];
    return options.filter((option) => values.includes(option.value));
};

// Property utility functions
export const getPropertyDefaultOption = (property: Property, options: DataListOption[]): DataListOption | undefined => {
    if (!property.defaultValue) {
        return undefined;
    }
    return findOptionById(options, Number(property.defaultValue));
};

export const getPropertyDefaultOptions = (property: Property, options: DataListOption[]): DataListOption[] => {
    if (!property.defaultValue) {
        return [];
    }

    const ids = property.defaultValue.split("|").map((id) => Number(id));

    return findOptionsByIds(options, ids);
};

export const getPropertyGenericOption = (property: Property, options: DataListOption[]): DataListOption | undefined => {
    if (!property.genericValue) {
        return undefined;
    }
    return findOptionById(options, Number(property.genericValue));
};

export const getPropertyGenericOptions = (property: Property, options: DataListOption[]): DataListOption[] => {
    if (!property.genericValue) {
        return [];
    }

    const ids = property.genericValue.split("|").map((id) => Number(id));

    return findOptionsByIds(options, ids);
};

// Option value utility functions
export const getOptionValue = (option: DataListOption | null | undefined): string => {
    return option?.value ?? "_empty_";
};

export const getOptionValues = (options: DataListOption[]): string => {
    return options.map((opt) => opt.value).join("|");
};

export const getOptionIds = (options: DataListOption[]): number[] => {
    return options.map((opt) => opt.id);
};

export const getOptionCodes = (options: DataListOption[]): string[] => {
    return options.map((opt) => opt.code);
};

export const getOptionGroupKey = (option: DataListOption): string => {
    const { optionGroup } = option;
    if (!optionGroup) {
        return UNGROUPED_OPTION_KEY;
    }
    if (typeof optionGroup === "string") {
        return optionGroup;
    }
    return optionGroup["@id"] ?? optionGroup.id.toString();
};

export const getOptionGroupLabel = (option: DataListOption): string => {
    const { optionGroup } = option;
    if (!optionGroup || typeof optionGroup === "string") {
        return "-";
    }
    return optionGroup.label ?? "-";
};

export const groupDataListOptions = (options: DataListOption[]): GroupedDataListOptions => {
    return options.reduce<GroupedDataListOptions>((groups, option) => {
        const groupKey = getOptionGroupKey(option);

        if (!groups[groupKey]) {
            groups[groupKey] = {
                label: getOptionGroupLabel(option),
                options: [],
            };
        }

        groups[groupKey].options.push(option);
        return groups;
    }, {});
};

export const filterGroupedDataListOptionsByLabel = (
    groups: GroupedDataListOptions,
    query: string,
): GroupedDataListOptions => {
    const foldedQuery = foldText(query);
    if (foldedQuery === "") return groups;

    return Object.fromEntries(
        Object.entries(groups)
            .map(([key, group]) => ({
                key,
                group: {
                    ...group,
                    options: group.options.filter((option) => foldText(option.label).includes(foldedQuery)),
                },
            }))
            .filter(({ group }) => group.options.length > 0)
            .map(({ key, group }) => [key, group]),
    );
};
